import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, User, ArrowRight, ArrowLeft, Check, Plus, Trash2, Instagram, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useApp, CONTENT_TYPES, PLATFORMS } from '@/context/AppContext';
import { authApi, creatorsApi, brandsApi, socialApi } from '@/services/api';
import { toast } from 'sonner';

const steps = [
  { id: 1, label: 'User Type' },
  { id: 2, label: 'Creator Profile' },
  { id: 3, label: 'Brands' },
  { id: 4, label: 'Connect Accounts' },
];

const STEP_VARIANTS = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { setUserType, setOnboardingComplete, setCurrentUser, setActiveCreatorId, setActiveBrandId, saveAuth } = useApp();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [selectedType, setSelectedType] = useState('');
  // Step 2
  const [creatorName, setCreatorName] = useState('');
  const [creatorEmail, setCreatorEmail] = useState('');
  const [password, setPassword] = useState('');
  // Step 3
  const [brands, setBrands] = useState([{ id: 'nb1', name: '', contentType: '' }]);
  // Step 4
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [agreeDisclaimer, setAgreeDisclaimer] = useState(false);
  const [agencyCreatorCount, setAgencyCreatorCount] = useState('1-5');

  const goNext = () => { setDirection(1); setStep(s => s + 1); };
  const goBack = () => { setDirection(-1); setStep(s => s - 1); };

  const handleFinish = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // 1. Register user
      const userEmail = creatorEmail || `${creatorName.toLowerCase().replace(/\s+/g, '.')}@reelia.io`;
      const userPassword = password || 'reelia123';
      let user;
      try {
        user = await authApi.register({
          name: creatorName,
          email: userEmail,
          password: userPassword,
          user_type: selectedType,
        });
      } catch (e) {
        // Try login if already registered
        try {
          user = await authApi.login({ email: userEmail, password: userPassword });
        } catch {
          throw e;
        }
      }
      setCurrentUser(user);
      saveAuth(user);
      setUserType(selectedType);

      // 2. Create creator profile
      const creator = await creatorsApi.create({
        name: creatorName,
        email: userEmail,
        agency_id: selectedType === 'agency' ? user.id : null,
      });

      // 3. Create brands
      let firstBrandId = null;
      for (const brand of brands.filter(b => b.name && b.contentType)) {
        const newBrand = await brandsApi.create({
          name: brand.name,
          content_type: brand.contentType,
          color: '#2F7BFF',
          creator_id: creator.id,
          platforms: connectedPlatforms,
        });
        if (!firstBrandId) firstBrandId = newBrand.id;

        // 4. Connect social accounts
        for (const platform of connectedPlatforms) {
          try {
            await socialApi.connect({
              platform,
              handle: `@${brand.name.toLowerCase().replace(/\s+/g, '_')}`,
              brand_id: newBrand.id,
            });
          } catch {}
        }
      }

      setActiveCreatorId(creator.id);
      if (firstBrandId) setActiveBrandId(firstBrandId);

      // Mark onboarding complete in backend
      try { await authApi.updateUser(user.id, { onboarding_complete: true }); } catch {}

      setOnboardingComplete(true);
      toast.success('Welcome to Reelia! Your workspace is ready.');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addBrand = () => {
    setBrands(prev => [...prev, { id: `nb${Date.now()}`, name: '', contentType: '' }]);
  };

  const updateBrand = (id, key, val) => {
    setBrands(prev => prev.map(b => b.id === id ? { ...b, [key]: val } : b));
  };

  const removeBrand = (id) => {
    setBrands(prev => prev.filter(b => b.id !== id));
  };

  const togglePlatform = (pid) => {
    setConnectedPlatforms(prev =>
      prev.includes(pid) ? prev.filter(p => p !== pid) : [...prev, pid]
    );
  };

  const canProceed = {
    1: !!selectedType,
    2: creatorName.trim().length > 0,
    3: selectedType === 'agency'
      ? agreeDisclaimer
      : brands.length > 0 && brands.every(b => b.name && b.contentType),
    4: connectedPlatforms.length > 0,
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      style={{ background: 'hsl(var(--background))' }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'var(--gradient-glow)' }} />
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1705107958696-a7f73c749ab3?crop=entropy&cs=srgb&fm=jpg&w=1400&q=60)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="relative z-10 w-full max-w-xl px-4">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-base"
            style={{ background: 'var(--gradient-primary)', color: '#fff' }}
          >R</div>
          <span className="text-xl font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Reelia</span>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-0 mb-8">
          {steps.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{
                    background: step > s.id ? 'hsl(var(--success))' : step === s.id ? 'hsl(var(--primary))' : 'hsl(var(--surface-elevated))',
                    color: step >= s.id ? '#fff' : 'hsl(var(--foreground-subtle))',
                    border: step < s.id ? '1px solid hsl(var(--border))' : 'none'
                  }}
                >
                  {step > s.id ? <Check size={12} /> : s.id}
                </div>
                <span className="text-xs mt-1 hidden sm:block"
                  style={{ color: step === s.id ? 'hsl(var(--foreground))' : 'hsl(var(--foreground-subtle))' }}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="w-12 h-px mx-1 mb-4"
                  style={{ background: step > s.id ? 'hsl(var(--primary))' : 'hsl(var(--border))' }} />
              )}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="reelia-card overflow-hidden" style={{ padding: '2rem' }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={step} variants={STEP_VARIANTS} initial="enter" animate="center" exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              {/* STEP 1 */}
              {step === 1 && (
                <div>
                  <h2 className="text-xl font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>Welcome to Reelia</h2>
                  <p className="text-sm mb-6" style={{ color: 'hsl(var(--foreground-muted))' }}>How will you be using Reelia?</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ type: 'creator', icon: User, title: 'Creator', desc: 'I create content for my own brands' },
                      { type: 'agency', icon: Users, title: 'Agency', desc: 'I manage content for multiple creators' }]
                      .map(opt => (
                        <button key={opt.type} onClick={() => setSelectedType(opt.type)}
                          className="p-5 rounded-xl border-2 text-left"
                          style={{
                            background: selectedType === opt.type ? 'hsl(var(--primary) / 0.08)' : 'hsl(var(--surface))',
                            borderColor: selectedType === opt.type ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                          }}
                        >
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                            style={{ background: 'hsl(var(--surface-elevated))', color: selectedType === opt.type ? 'hsl(var(--primary))' : 'hsl(var(--foreground-muted))' }}
                          >
                            <opt.icon size={18} />
                          </div>
                          <p className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>{opt.title}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>{opt.desc}</p>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div>
                  <h2 className="text-xl font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>Creator Profile</h2>
                  <p className="text-sm mb-5" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    {selectedType === 'agency' ? 'Set up your agency account' : 'Tell us about yourself'}
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Full Name *</Label>
                      <input className="reelia-input" placeholder="e.g. Alex Rivera" value={creatorName} onChange={e => setCreatorName(e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Email</Label>
                      <input className="reelia-input" placeholder="you@example.com" value={creatorEmail} onChange={e => setCreatorEmail(e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Password</Label>
                      <input className="reelia-input" type="password" placeholder="Create a password" value={password} onChange={e => setPassword(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3 — Agency: creator count + disclaimer | Creator: brand setup */}
              {step === 3 && selectedType === 'agency' && (
                <div>
                  <h2 className="text-xl font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>Agency Setup</h2>
                  <p className="text-sm mb-5" style={{ color: 'hsl(var(--foreground-muted))' }}>Tell us about your agency's scale</p>
                  <div className="space-y-5">
                    <div>
                      <Label className="text-xs mb-2 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                        How many creators do you manage?
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {['1-5', '6-20', '21-50', '51-100', '100-500', '500+'].map(range => (
                          <button
                            key={range}
                            onClick={() => setAgencyCreatorCount(range)}
                            data-testid={`creator-count-${range}`}
                            className="px-3 py-2 rounded-lg text-sm font-medium transition-all"
                            style={{
                              background: agencyCreatorCount === range ? 'hsl(var(--primary) / 0.12)' : 'hsl(var(--surface))',
                              border: `1px solid ${agencyCreatorCount === range ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                              color: agencyCreatorCount === range ? 'hsl(var(--primary))' : 'hsl(var(--foreground-muted))',
                            }}
                          >
                            {range}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 rounded-xl" style={{ background: 'hsl(var(--warning) / 0.06)', border: '1px solid hsl(var(--warning) / 0.35)' }}>
                      <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'hsl(var(--warning))' }}>
                        Agency Responsibility Disclaimer
                      </p>
                      <p className="text-xs mb-3 leading-relaxed" style={{ color: 'hsl(var(--foreground-muted))' }}>
                        By proceeding, you confirm that you have obtained proper authorisation from all creators whose accounts you manage. You assume full legal and ethical responsibility for all content published on their behalf, including copyright compliance and platform terms of service.
                      </p>
                      <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="agency-disclaimer-checkbox">
                        <input
                          type="checkbox"
                          checked={agreeDisclaimer}
                          onChange={e => setAgreeDisclaimer(e.target.checked)}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <span className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                          I understand and accept full responsibility for all managed creator accounts.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && selectedType === 'creator' && (
                <div>
                  <h2 className="text-xl font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>Create Brands</h2>
                  <p className="text-sm mb-4" style={{ color: 'hsl(var(--foreground-muted))' }}>Each brand is a separate content identity</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {brands.map((brand, idx) => (
                      <div key={brand.id} className="p-3 rounded-lg" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium" style={{ color: 'hsl(var(--foreground-muted))' }}>Brand {idx + 1}</span>
                          {brands.length > 1 && (
                            <button onClick={() => removeBrand(brand.id)} className="ml-auto p-1 rounded hover:bg-surface-hover"
                              style={{ color: 'hsl(var(--foreground-subtle))' }}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <input
                            className="reelia-input text-xs h-8 flex-1"
                            placeholder="Brand name"
                            value={brand.name}
                            onChange={e => updateBrand(brand.id, 'name', e.target.value)}
                          />
                          <select className="reelia-input text-xs h-8 flex-1" value={brand.contentType}
                            onChange={e => updateBrand(brand.id, 'contentType', e.target.value)}
                          >
                            <option value="">Content type</option>
                            {CONTENT_TYPES.map(ct => (
                              <option key={ct.id} value={ct.id}>{ct.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={addBrand} className="flex items-center gap-2 text-xs mt-3"
                    style={{ color: 'hsl(var(--primary))' }}
                  >
                    <Plus size={14} /> Add another brand
                  </button>
                </div>
              )}

              {/* STEP 4 */}
              {step === 4 && (
                <div>
                  <h2 className="text-xl font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>Connect Accounts</h2>
                  <p className="text-sm mb-4" style={{ color: 'hsl(var(--foreground-muted))' }}>Connect at least one social account to publish</p>
                  <div className="space-y-2 mb-4">
                    {PLATFORMS.map(p => (
                      <button key={p.id} onClick={() => togglePlatform(p.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg"
                        style={{
                          background: connectedPlatforms.includes(p.id) ? 'hsl(var(--primary) / 0.08)' : 'hsl(var(--surface))',
                          border: `1px solid ${connectedPlatforms.includes(p.id) ? 'hsl(var(--primary) / 0.4)' : 'hsl(var(--border))'}`,
                        }}
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                          style={{ background: 'hsl(var(--surface-elevated))', color: 'hsl(var(--foreground-muted))' }}>
                          {p.id.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="text-sm flex-1 text-left" style={{ color: 'hsl(var(--foreground))' }}>{p.label}</span>
                        {connectedPlatforms.includes(p.id) && (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'hsl(var(--primary))' }}>
                            <Check size={11} color="#fff" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {/* Step 4 focuses only on platform connections */}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5" disabled={loading}>
                <ArrowLeft size={14} /> Back
              </Button>
            ) : <div />}
            {step < steps.length ? (
              <Button size="sm" disabled={!canProceed[step]} onClick={goNext} className="gap-1.5"
                style={{ background: canProceed[step] ? 'hsl(var(--primary))' : 'hsl(var(--surface-elevated))', color: '#fff' }}
              >
                Continue <ArrowRight size={14} />
              </Button>
            ) : (
              <Button size="sm" disabled={!canProceed[step] || loading} onClick={handleFinish} className="gap-1.5"
                style={{ background: canProceed[step] && !loading ? 'hsl(var(--success))' : 'hsl(var(--surface-elevated))', color: '#fff' }}
              >
                {loading ? 'Setting up...' : <><Check size={13} /> Enter Reelia</>}
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-xs mt-4" style={{ color: 'hsl(var(--foreground-subtle))' }}>
          7-day free trial · No credit card required
        </p>
      </div>
    </div>
  );
}
